<script>
export default {
	name: 'LowFareDestinationTiles',
	inheritAttrs: false,
};
</script>

<script setup>
import dayjs from 'dayjs';
import CTAButton from '~/aem-components/CTAButton.vue';
import { isMobileViewport } from '~/logic/composables/breakpoints';
import { useGlobalAEMState } from '~/logic/aem/global-aem-state.js';
import usePersistencyWithExpiry from '~/logic/composables/usePersistencyWithExpiry.js';
import { useEventBus } from '@vueuse/core';
import { MHScrollToElement } from '~/logic/helpers/dom.js';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import Dropdown from '~/components/form/Dropdown.vue';
import { formatNumberWithAbbreviation } from '~/logic/helpers/string.js';


dayjs.extend(isSameOrAfter);

const globalBookFlightBus = useEventBus('booking-widget:book-flight');

const props = defineProps({
	title: { type: String, default: '' },
	type: { type: String, default: 'varient1' },
	destinationText: { type: String, default: '' },
	destinationUrl: { type: String, default: '' },
	isPersonalizationOn: { type: Boolean, default: false },
	fromLabel: { type: String, default: '' },

	bookDate: { type: Number, default: 0 },
	isHideInDesktop: { type: Boolean, default: false },
	isHideInMobile: { type: Boolean, default: false },
	componentIdAA: { type: String, default: '' },

	economyLabel: { type: String, default: '' },
	bookNowText: { type: String, default: '' },
	exploreText: { type: String, default: '' },
	personalizationLabel: { type: String, default: '' },
	lowFareList: { type: Array, default: () => ([]) },

});
const emit = defineEmits([
	// 'update:modelValue',
]);

const originPickerEl = ref(null);
const originVal = ref(props.lowFareList.length ? props.lowFareList[0].originAirportCode : '');
const pastSearches = ref([]);

const lockedItem = ref([]);

const computedOriginList = computed(() => {
	return props.lowFareList.map(({ originCityName, originAirportCode, lowFarePrice }) => {
		return {
			displayLabel: originCityName,
			value: originAirportCode,
			lowFarePrice: lowFarePrice,
		};
	});
});

const toggleLock = (id) => {
	if (lockedItem.value.includes(id)) {
		lockedItem.value.splice(lockedItem.value.indexOf(id), 1);
	} else {
		lockedItem.value.push(id);
	}
};

watch(originVal, (v) => {
	lockedItem.value = [];
});

const { isEditorMode, pageProperties } = useGlobalAEMState();

usePersistencyWithExpiry('flight-search:past-searches', {
	refs: {
		pastSearches,
	},
});

const openFlightSearch = (({ from, to }) => {
	globalBookFlightBus.emit('active-tab-and-fill-up-form', { mainTab: 'flight-search-tab', locationFrom: from, locationTo: to });
});

const computedCompleteDestinationList = computed(() => {
	return props.lowFareList.find((item) => {
		return item.originAirportCode === originVal.value;
	})?.destinationList;
});

const modifiedList = computed(() => {
	if (!props.isPersonalizationOn) return computedCompleteDestinationList.value;

	const result = computedCompleteDestinationList.value.map((item) => {
		let isRecommended = false;
		pastSearches.value?.forEach((pastSearch) => {
			if (pastSearch?.ondValueTo?.value) {
				if ((originVal.value === pastSearch.ondValueFrom.value) && (item?.destinationAirportCode === pastSearch.ondValueTo.value)) {
					isRecommended = true;
				}
			}
		});
		return { ...item, isRecommended: isRecommended };
	});
	
	return result.sort(function (x, y) {
		return y.isRecommended - x.isRecommended;
	});

});

</script>



<template>
<template v-if="isEditorMode && props.isHideInDesktop">
	<div class="generic-container py-10 text-center">
		<p>Hidden in: <span v-if="props.isHideInDesktop">Desktop</span> <span v-if="props.isHideInMobile">, Mobile</span></p>
	</div>
</template>

<template v-else>
	<div class="LowFareDestinationTiles generic-container">
		<div :class="{ '>md:hidden': isHideInDesktop, 'hidden >md:block': isHideInMobile }">
			<div class="text-center text-neutral-black-base">
				<h2 class="pb-4 pt-4">{{ props.title }}</h2>
				<div class="pb-10 inline-flex">
					<span v-html-sanitize="props.fromLabel" class="mr-2"></span>
					<Dropdown
						v-if="computedOriginList.length > 1"
						ref="originPickerEl"
						v-model="originVal"
						:rootAttrs="{
							class: 'origin-picker',
						}"
						name="originPicker"
						:labelText="props.general?.label"
						:options="computedOriginList"
						@update:model-value="originVal = $event"
					>
						<template #placeholder>
							<div class="placeholder-wrapper relative flex-grow mr-2 leading-none">
								<span class="text-label font-semibold">{{ originVal }}</span>
							</div>
						</template>

						<template #option="{ option }">
							<span class="text-value relative flex items-center">{{ option.displayLabel }}</span>
						</template>
						
						<template #singlelabel="{ value }">
							<div class="value-wrapper multiselect-single-label flex-grow mr-2 rtl:ml-2 rtl:mr-0 leading-none font-semibold">
								<span class="text-value relative flex items-center">{{ value.displayLabel }}</span>
							</div>
						</template>

						<template #caret>
							<!-- 👇 This is the actual caret -->
							<icon-fas-caret-down v-if="computedOriginList.length > 1" class="dropdown-caret mt-1 mr-7 md:mr-3 md:rtl:ml-3 z-50 relative transition-transform duration-200 fill-primary-blue-base" />
						</template>

						<template #mobile-title>
							<span v-html-sanitize="props.fromLabel"></span>
						</template>
					</Dropdown>
					<span v-else>{{ props.general?.label || 'Kuala Lumpur' }}</span>
				</div>
			</div>
			<div v-if="props.type === 'varient1'">
				<div class="stack-container pb-4">
					<div v-if="modifiedList.length" class="w-1/2 col-highlight md:hidden">
						<div
							:class="{
								'locked': lockedItem.includes('highlight') && !isMobileViewport,
							}"
							class="low-fare-dest-tiles-wrapper group"
							@click="toggleLock('highlight')"
						>
							<div class="low-fare-dest-tiles-content-container">
								<div class="inner-tile-overlay"></div>
								<div
									class="low-fare-dest-tiles-content flex flex-col items-start justify-between text-neutral-white-base px-7 py-6"
									:style="{
										'background-image': `url('${modifiedList[0]?.destinationTileImage}')`,
									}"
									:title="`${modifiedList[0]?.destinationName} (${ modifiedList[0]?.destinationAirportCode })`"
								>
									<span class="sr-only">{{ modifiedList[0]?.destinationName }} ({{ modifiedList[0]?.destinationAirportCode }}) </span>

									<div
										class="low-fare-dest-tiles-content-tag"
										:class="{ 'invisible': !modifiedList[0]?.isRecommended }"
									>
										<div>
											<p class="line-clamp-1">{{ props.personalizationLabel || $t('Recommended For You') }}</p>
										</div>
									</div>
									
									<div class="tile-text-content w-full">
										<p class="destination-title heading-4 md:heading-6">{{ modifiedList[0]?.destinationName }}</p>
										<p
											:class="{
												'invisible': parseInt(modifiedList[0]?.lowFarePrice) <= 0,
											}"
											class="cabin-class-txt font-semibold"
										>
											{{ props.economyLabel }}
										</p>
										<p
											:class="{
												'invisible': parseInt(modifiedList[0]?.lowFarePrice) <= 0,
											}"
											class="price-txt heading-4 md:heading-6"
										>
											{{ modifiedList[0]?.currencyCode }} {{ formatNumberWithAbbreviation(Math.round(parseFloat(modifiedList[0]?.lowFarePrice)), 5) }}
										</p>
										
										<div class="low-fare-dest-tiles-content-btn-group">
											<CTAButton
												v-aa="[
													{
														clickName: props.bookNowText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: modifiedList[0]?.destinationAirportCode,
													},
													{
														type: 'other',
														name: props.bookNowText,
													},
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -1px;"
												styleType="primary"
												:ctaText="props.bookNowText"
												@click="openFlightSearch({ from: originVal, to: modifiedList[0]?.destinationAirportCode })"
											></CTAButton>
											<CTAButton
												v-aa="[
													{
														clickName: props.exploreText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: modifiedList[0]?.destinationAirportCode,
													},
													{
														type: 'other',
														name: props.exploreText,
													},
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -2px;"
												styleType="secondary"
												:ctaText="props.exploreText"
												:href="modifiedList[0]?.destinationDetailsPageUrl"
											></CTAButton>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>

					<div class="w-1/2 md:w-full col-stack-with-grid">
						<div
							v-for="(item, index) in (isMobileViewport ? modifiedList : modifiedList.slice(1))"
							:key="index"
							:class="{
								'hidden': index >= 6,
								'locked': lockedItem.includes(`${originVal}${index}`) && !isMobileViewport,
							}"
							class="low-fare-dest-tiles-wrapper group"
							@click="toggleLock(`${originVal}${index}`)"
						>
							<div class="low-fare-dest-tiles-content-container">
								<div class="inner-tile-overlay"></div>
								<div
									class="low-fare-dest-tiles-content flex flex-col items-start justify-between text-neutral-white-base px-7 py-6 lg:p-4"
									:style="{
										'background-image': `url('${item.destinationTileImage}')`,
									}"

									:title="`${item.destinationName} (${ item.destinationAirportCode })`"
								>
									<span class="sr-only">{{ item.destinationName }} ({{ item.destinationAirportCode }})</span>
									<div
										class="low-fare-dest-tiles-content-tag invisible"
									>
										<div>
											<p class="line-clamp-1">{{ props.personalizationLabel || $t('Recommended For You') }}</p>
										</div>
									</div>
									
									<div class="tile-text-content w-full">
										<p class="destination-title heading-4 md:heading-6">{{ item.destinationName }}</p>
										<p
											:class="{
												'invisible': parseInt(item.lowFarePrice) <= 0,
											}"
											class="cabin-class-txt font-semibold"
										>
											{{ props.economyLabel }}
										</p>
										<p
											:class="{
												'invisible': parseInt(item.lowFarePrice) <= 0,
											}"
											class="price-txt heading-4 md:heading-6"
										>
											{{ item.currencyCode }} {{ formatNumberWithAbbreviation(Math.round(parseFloat(item.lowFarePrice)), 5) }}
										</p>

										<div class="low-fare-dest-tiles-content-btn-group">
											<CTAButton
												v-aa="[
													{
														clickName: props.bookNowText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{
														type: 'other',
														name: props.bookNowText,
													},
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -1px;"
												styleType="primary"
												:ctaText="props.bookNowText"
												@click="openFlightSearch({ from: originVal, to: item.destinationAirportCode })"
											></CTAButton>
											<CTAButton
												v-aa="[
													{
														clickName: props.exploreText,
														clickComponentType: 'Button',
														componentName: 'LowFareDestinationTiles',
														componentID: props.componentIdAA,
														origin: originVal.value,
														destination: item.destinationAirportCode,
													},
													{
														type: 'other',
														name: props.exploreText,
													},
												]"
												containerClass="low-fare-dest-tiles-content-btn flex-grow"
												containerStyle="--focus-visible-outline-offset: -2px;"
												styleType="secondary"
												:ctaText="props.exploreText"
												:href="item.destinationDetailsPageUrl"
											></CTAButton>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<CTAButton
					containerStyle="--focus-visible-outline-offset: -2px;"
					containerClass="pt-10 pb-4"
					:ctaText="props.destinationText"
					:href="props.destinationUrl"
					sizeType="medium"
					styleType="primary"
				>
				</CTAButton>
			</div>
		</div>
	</div>
</template>
</template>

<style lang="scss">
.OverlayBtmSticky .Dropdown .multiselect-main .text-value {
	top: 0;
}
</style>

<style scoped lang="scss">
@use 'sass:color';
@use '~/styles/partials/_var.scss';

.LowFareDestinationTiles {
	:deep(.origin-picker) {
		margin-top: -1px;

		.multiselect {
			background: transparent;
			border: 0;
		}

		.multiselect-search-wrapper {
			position: static;
		}

		.multiselect-main {
			height: 25px;

			.text-value {
				top: 0;
			}

			.multiselect-single-label {
				position: static;
				padding: 0 !important;
				margin: 0;
				margin-right: 5px;
				border-bottom: 1px solid;

				.text-value {
					top: 5px !important;
				}
			}
		}

		.multiselect-dropdown {
			width: 290px !important;
		}
	}

	.low-fare-dest-tiles-content-btn-group {
		@apply flex flex-col gap-3 lg:gap-[10px] w-full mt-3;
	}

	.low-fare-dest-tiles-content-btn-group {
		visibility: hidden;
		opacity: 0;
		margin-bottom: -50px;
		transition: all ease 400ms;

		:deep(.CTAButton) {
			@apply text-sm lg:text-xs py-3 lg:py-1.5 px-4 w-full font-semibold;
		}
	}

	.col-stack-with-grid .low-fare-dest-tiles-content-btn-group {
		margin-bottom: -125px;

		@media #{var.$query-max-lg} {
			margin-bottom: -80px;
		}
	}

	:deep(.tile-text-content),
	:deep(.low-fare-dest-tiles-content-tag) {
		position: relative;
		z-index: 10;
	}

	.stack-container {
		@apply flex gap-6;
	}

	.low-fare-dest-tiles-wrapper {
		position: relative;
		width: 100%;
		aspect-ratio: 1/1.25;
		opacity: 1;
		user-select: none;
		transition: filter .5s;
		filter: drop-shadow(0 3px 7px transparent);

		@media #{var.$query-min-md} {
			aspect-ratio: 1/1.5;
		}

		@media #{var.$query-min-lg} {
			aspect-ratio: 1/1.25;
		}
		
		@media #{var.$query-min-xl} {
			aspect-ratio: 1/1;
		}
		
		&:focus-visible {
			outline: none;
		}
		
		&:hover, &:focus-within, &.locked {
			.inner-tile-overlay {
				background-color: rgba(black, 0.5);
			}

			:deep(.low-fare-dest-tiles-content-btn-group) {
				visibility: visible;
				opacity: 1;
				margin-bottom: 0px;
			}
		}
	}

	.low-fare-dest-tiles-content-container {
		@apply inset-x-0;
		position: absolute;
		top: 0;
		width: 100%;
		height: 100%;
		overflow: hidden;
		border-radius: 24px;

		@media #{var.$query-max-lg} {
			border-radius: 12px;
		}

	}

	.low-fare-dest-tiles-content {
		background-size: cover;
		background-repeat: no-repeat;
		width: 100%;
		height: 100%;
		position: relative;
		background-color: var(--neutral-grey-light);
	}

	.inner-tile-overlay {
		@apply mx-auto inset-x-0;
		background-color: rgba(black, 0.2);
		position: absolute;
		z-index: 10;
		top: 0;
		width: 100%;
		height: 100%;
		transition: all ease 400ms;
	}

	.low-fare-dest-tiles-content-tag {
		@apply bg-neutral-white-base text-primary-blue-base rounded px-2 text-xs font-bold py-0.5;
	}

	.destination-title {
		@apply line-clamp-2;
	}

	.cabin-class-txt {
		@apply line-clamp-1;

		@media #{var.$query-max-lg} {
			@apply text-xs;
		}
	}

	.price-txt {
		@apply line-clamp-1;

		@media #{var.$query-max-lg} {
			@apply text-xl;
		}
	}

	.date-txt {
		@apply line-clamp-1;

		@media #{var.$query-max-lg} {
			@apply text-xs;
		}
	}

	.col-highlight {
		.low-fare-dest-tiles-wrapper {
			height: 100%;
		}

		.destination-title,
		.price-txt {
			font-size: 2.5rem;
			line-height: 3rem;
		}

		.cabin-class-txt,
		.date-txt {
			font-size: 1.25rem;
			line-height: 3rem;
		}

		.low-fare-dest-tiles-content-btn-group {
			@apply flex-row;
		}
	}

	.col-stack-with-grid {
		display: grid;
		grid-template-columns: repeat(2, 1fr);
		gap: 20px;
		max-width: 1200px;
		margin: 0 auto;

		@media #{var.$query-min-md} {
			grid-template-columns: repeat(auto-fit, minmax(calc(50% - 20px), 1fr));

			.low-fare-dest-tiles-wrapper:only-child {
				width: calc(50% - 10px);
			}
		}
	}
}
</style>
